import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';

import type { ColorType } from '@lib/types';
import { setThisClass } from '@lib/utils';

type TabOrientation = 'horizontal' | 'vertical';
type TabVariant = 'default' | 'pills' | 'underline';

type TabContextType = {
  activeTab: string;
  setActiveTab: (value: string) => void;
  orientation: TabOrientation;
  variant: TabVariant;
  color: ColorType;
};

const TabContext = createContext<TabContextType | null>(null);

function useTabContext() {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('Tab components must be used within a TabGroup');
  }
  return context;
}

export type TabGroupProps = {
  children: ReactNode;
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  orientation?: TabOrientation;
  variant?: TabVariant;
  color?: ColorType;
  className?: string;
};

export type TabListProps = {
  children: ReactNode;
  className?: string;
};

export type TabProps = {
  children: ReactNode;
  value: string;
  disabled?: boolean;
  icon?: ReactNode;
  badge?: ReactNode;
  className?: string;
};

export type TabPanelsProps = {
  children: ReactNode;
  className?: string;
};

export type TabPanelProps = {
  children: ReactNode;
  value: string;
  className?: string;
};

function TabGroup({
  children,
  defaultValue = '',
  value: controlledValue,
  onChange,
  orientation = 'horizontal',
  variant = 'default',
  color = 'primary',
  className,
}: TabGroupProps) {
  const [internalValue, setInternalValue] = useState(defaultValue);

  const isControlled = controlledValue !== undefined;
  const activeTab = isControlled ? controlledValue : internalValue;

  const setActiveTab = useCallback(
    (value: string) => {
      if (!isControlled) {
        setInternalValue(value);
      }
      onChange?.(value);
    },
    [isControlled, onChange]
  );

  return (
    <TabContext.Provider value={{ activeTab, setActiveTab, orientation, variant, color }}>
      <div
        className={clsx(
          'flex',
          orientation === 'vertical' ? 'flex-row gap-4' : 'flex-col',
          className
        )}
      >
        {children}
      </div>
    </TabContext.Provider>
  );
}

function TabList({ children, className }: TabListProps) {
  const { orientation, variant } = useTabContext();

  return (
    <div
      role="tablist"
      className={clsx(
        'flex',
        orientation === 'vertical' ? 'flex-col gap-1' : 'flex-row gap-1',
        variant === 'underline' &&
          orientation === 'horizontal' &&
          'border-b border-gray-200 dark:border-dark-600',
        className
      )}
    >
      {children}
    </div>
  );
}

function Tab({ children, value, disabled = false, icon, badge, className }: TabProps) {
  const { activeTab, setActiveTab, variant, color } = useTabContext();
  const isActive = activeTab === value;

  const handleClick = () => {
    if (!disabled) {
      setActiveTab(value);
    }
  };

  const variantStyles = {
    default: clsx(
      'px-4 py-2 rounded-lg transition-colors',
      isActive
        ? [setThisClass(color), 'bg-this/10 text-this dark:bg-this-light/20 dark:text-this-lighter']
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-dark-300 dark:hover:text-dark-100 dark:hover:bg-dark-700'
    ),
    pills: clsx(
      'px-4 py-2 rounded-full transition-colors',
      isActive
        ? [setThisClass(color), 'bg-this text-white dark:bg-this-light']
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-dark-300 dark:hover:text-dark-100 dark:hover:bg-dark-700'
    ),
    underline: clsx(
      'px-4 py-2 relative transition-colors',
      isActive
        ? [setThisClass(color), 'text-this dark:text-this-lighter']
        : 'text-gray-600 hover:text-gray-900 dark:text-dark-300 dark:hover:text-dark-100',
      isActive &&
        'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-this dark:after:bg-this-light'
    ),
  };

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      aria-disabled={disabled}
      onClick={handleClick}
      disabled={disabled}
      className={clsx(
        'flex items-center gap-2 font-medium text-sm',
        variantStyles[variant],
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {icon && <span className="shrink-0">{icon}</span>}
      {children}
      {badge && <span className="shrink-0">{badge}</span>}
    </button>
  );
}

function TabPanels({ children, className }: TabPanelsProps) {
  return <div className={clsx('flex-1', className)}>{children}</div>;
}

function TabPanel({ children, value, className }: TabPanelProps) {
  const { activeTab } = useTabContext();

  if (activeTab !== value) return null;

  return (
    <div role="tabpanel" className={clsx('animate-in fade-in duration-200', className)}>
      {children}
    </div>
  );
}

TabGroup.displayName = 'TabGroup';
TabList.displayName = 'TabList';
Tab.displayName = 'Tab';
TabPanels.displayName = 'TabPanels';
TabPanel.displayName = 'TabPanel';

export { TabGroup, TabList, Tab, TabPanels, TabPanel };
